import {
  ChatStates,
  selectChatState,
  setVirtualHumanTalking,
} from '@vh/slices/chat/ChatSlice';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VHInfoPopup from '@vh/components/VHInfoPopup/VHInfoPopup';
import {
  HeyGenAvatarVideo,
  ReconnectWrapper,
} from '@vh/components/VHPlayer/HeyGenVHPlayer/HeyGenPlayer.styled';
import { useEffectOnlyOnce } from '@vh/utility/EventHooks';
import {
  setIsForceDisabled,
  setMoreInfoVisible,
} from '@vh/slices/vh-player/VHPlayerSlice';
import { virtualHumanDataSelector } from '@vh/slices/VirtualHumanSlice';
import useFlashMessage from '@core/components/FlashMessage';
import { useHeyGen } from '@vh/components/VHPlayer/HeyGenVHPlayer/useHeyGen';
import { Button } from '@virtidev/toolbox';
import { useOnNewTTSFriendlyVHText } from '@vh/components/VHPlayer/useOnNewTTSFriendlyVHText';
import { useAutoStartMicStream } from '@core/components/MicrophoneStreamProvider/useAutoStartMicStream';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFeature from '@core/components/LaunchDarkly';
import LegacyVHInfoPopup from '@vh/components/VHInfoPopup/LegacyVHInfoPopup';
import { VHPlayerEventBusContext } from '@vh/contexts/VHPlayerEventBus/VHPlayerEventBus.context';

/** @type {React.FC<{vhId: number; onLoaded: () => void;}>} */
const HeyGenPlayer = ({ vhId, onLoaded }) => {
  const dispatch = useDispatch();
  /** @type {React.MutableRefObject<HTMLVideoElement | null>} */
  const videoRef = useRef(null);

  const handleAvatarStartedTalking = useCallback(() => {
    dispatch(setVirtualHumanTalking(true));
  }, [dispatch]);

  const handleAvatarFinishedTalking = useCallback(() => {
    dispatch(setVirtualHumanTalking(false));
  }, [dispatch]);
  useAutoStartMicStream(false);

  const [connected, setConnected] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const vh = useSelector(virtualHumanDataSelector);
  const chatState = useSelector(selectChatState);
  const chatStateRef = useRef(chatState);
  chatStateRef.current = chatState;

  const hasLoadedRef = useRef(false);
  const handleConnected = useCallback(() => {
    console.log('Connected');
    dispatch(setIsForceDisabled(false));
    setConnected(true);
    setNeedsReconnect(false);
    if (onLoaded && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      onLoaded();
    }
  }, [dispatch, onLoaded]);

  const [error, setError] = useState(null);
  const handleDisconnected = useCallback(() => {
    dispatch(setVirtualHumanTalking(false));
    dispatch(setIsForceDisabled(true));
    setConnected(false);
    if (
      chatStateRef.current === ChatStates.TALK ||
      chatStateRef.current === ChatStates.AWAITING_FIRST_CHAT
    ) {
      setNeedsReconnect(true);
    }
  }, [dispatch]);

  const handleError = useCallback((err) => {
    setConnected(false);
    setError(err + '');
  }, []);

  const { search } = useLocation();
  const timeoutMSFromURL = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('timeoutMS');
  }, [search]);

  const {
    interruptAvatar,
    initializeAvatarSession,
    sendTextToAvatar,
    terminateAvatarSession,
  } = useHeyGen(videoRef, {
    // voiceId: vh.VHVoice?.Code ?? undefined,
    avatarName: vh.HeyGenAvatar?.Name ?? 'c20f4bdddbe041ecba98d93444f8b29b',
    onError: handleError,
    onDisconnected: handleDisconnected,
    onConnected: handleConnected,
    onAvatarStartedTalking: handleAvatarStartedTalking,
    onAvatarFinishedTalking: handleAvatarFinishedTalking,
    timeoutMS: timeoutMSFromURL ? Number(timeoutMSFromURL) : 5 * 60 * 1000, // 5 mins
  });

  const { on } = useContext(VHPlayerEventBusContext);
  useEffect(() => {
    const subscribe = on('StopVirtualHumanTalking', () => {
      interruptAvatar();
    });
    return subscribe;
  }, [interruptAvatar, on]);

  const handleOnNewVHText = useCallback(
    (newText) => {
      sendTextToAvatar(newText);
      dispatch(setVirtualHumanTalking(true));
    },
    [dispatch, sendTextToAvatar]
  );
  useOnNewTTSFriendlyVHText(handleOnNewVHText);

  useEffect(() => {
    return () => {
      terminateAvatarSession();
    };
  }, [terminateAvatarSession]);

  useEffectOnlyOnce(() => {
    dispatch(setMoreInfoVisible(true));
  });

  useEffect(() => {
    if (chatState === ChatStates.COMPLETE) {
      terminateAvatarSession();
    }
  }, [chatState, terminateAvatarSession]);

  const connectedRef = useRef(connected);
  connectedRef.current = connected;
  const errorRef = useRef(error);
  errorRef.current = error;

  const { addFlashMessage } = useFlashMessage();

  useEffect(() => {
    if (error) {
      addFlashMessage(error + '', 'error');
    }
  }, [addFlashMessage, error]);

  const connectToHeygen = useCallback(async () => {
    initializeAvatarSession();
    dispatch(setVirtualHumanTalking(false));
    await (async () => {
      setError(null);
      setConnecting(true);
      return new Promise((resolve, reject) => {
        const interval = setInterval(() => {
          if (connectedRef.current || errorRef.current) {
            clearInterval(interval);
            setConnecting(false);
            if (errorRef.current) {
              reject(errorRef.current);
            } else {
              resolve();
            }
          }
        }, 100);
      });
    })();
  }, [dispatch, initializeAvatarSession]);

  const handleOnBeforeStart = useCallback(async () => {
    await connectToHeygen();
  }, [connectToHeygen]);

  const handleReconnect = useCallback(async () => {
    await connectToHeygen();
  }, [connectToHeygen]);

  const { t } = useTranslation();

  const showSelectableUserLanguageFeature = useFeature(
    'show-selectable-user-language'
  );

  return (
    <>
      {showSelectableUserLanguageFeature ? (
        <VHInfoPopup onBeforeStart={handleOnBeforeStart} vhId={vhId} />
      ) : (
        <LegacyVHInfoPopup onBeforeStart={handleOnBeforeStart} vhId={vhId} />
      )}
      {needsReconnect && (
        <ReconnectWrapper>
          <Button
            onClick={handleReconnect}
            loading={connecting}
            color="blue"
            icon="loop"
          >
            {t('reconnect')}
          </Button>
        </ReconnectWrapper>
      )}
      <HeyGenAvatarVideo ref={videoRef} autoPlay playsInline />
    </>
  );
};

export default HeyGenPlayer;
